/**
 * Guardia de conectividad.
 *
 * ADR-006: sin conexión, la app no deja empezar. Como no hay borradores
 * locales, bloquear ANTES de que el usuario teclee es la única forma de
 * que no pierda trabajo.
 *
 * `navigator.onLine` NO basta: es optimista y devuelve true con solo tener
 * interfaz de red, aunque no haya internet real (WiFi de cafetería con
 * portal cautivo, datos agotados, avión con WiFi de pago). Por eso la
 * verificación real es un ping al servidor.
 */

export type ConnectionState =
  | { status: 'online' }
  | { status: 'offline'; problem: 'no_network' | 'server_unreachable' };

/** Endpoint ligero: solo confirma que el backend responde. */
const HEALTH_PATH = '/api/health';

/** Más de esto se siente roto; el usuario merece respuesta rápida. */
const HEALTH_TIMEOUT_MS = 5_000;

/**
 * Comprueba conectividad real contra el servidor.
 *
 * Distingue los dos problemas porque el usuario no puede hacer lo mismo
 * ante cada uno: sin red, se conecta; con el servidor caído, esperar es
 * lo único posible y decirle "revisa tu conexión" sería mentirle.
 */
export async function checkConnection(
  fetchImpl: typeof fetch = fetch,
): Promise<ConnectionState> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { status: 'offline', problem: 'no_network' };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);

  try {
    const res = await fetchImpl(HEALTH_PATH, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });

    if (!res.ok) {
      return { status: 'offline', problem: 'server_unreachable' };
    }
    return { status: 'online' };
  } catch {
    // Un fallo de red aquí con navigator.onLine en true suele ser un
    // portal cautivo o datos agotados: para el usuario, no hay internet.
    return { status: 'offline', problem: 'no_network' };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Acciones que exigen conexión verificada ANTES de que el usuario empiece.
 *
 * Sin esta lista, el bloqueo llegaría cuando ya escribió — el peor momento
 * posible de la app, y el que ADR-006 nos obliga a evitar por diseño.
 */
export const REQUIRES_CONNECTION = [
  'escribir_recomendacion',
  'agregar_lugar',
  'reclamar_visit_token',
  'validar_lugar',
  'crear_coleccion',
  'guardar_coleccion',
  'autenticarse',
] as const;

export type ConnectionGatedAction = (typeof REQUIRES_CONNECTION)[number];

export function requiresConnection(action: string): boolean {
  return (REQUIRES_CONNECTION as readonly string[]).includes(action);
}
